import React, {
	FC, useCallback, useEffect, useRef, useState, 
} from 'react';
import {
	CancelDrop,
	closestCenter,
	CollisionDetection,
	DndContext,
	getFirstCollision,
	KeyboardCoordinateGetter,
	KeyboardSensor,
	Modifiers,
	MouseSensor,
	pointerWithin,
	rectIntersection,
	TouchSensor,
	UniqueIdentifier,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	AnimateLayoutChanges,
	arrayMove,
	defaultAnimateLayoutChanges,
	horizontalListSortingStrategy,
	rectSwappingStrategy,
	SortableContext,
	SortingStrategy,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSelector } from 'react-redux';
import { coordinateGetter as multipleContainersCoordinateGetter } from '../../multipleContainersKeyboardCoordinates';
import { Container, ContainerProps } from '../Container';
import { Item } from '../Item';
import { useMountStatus } from '../../../../../../hooks/useMountStatus';
import { measuring } from '../../model/constants';
import { ITag } from '../../../../../../entities/module/private-template-module-tag/api/types';
import { ITagGroup, TagGroupType } from '../../../../../../entities/template/private-template-tag-group/model/types';
import { templateTagGroupActions } from '../../../../../../entities/template/private-template-tag-group';
import { useUpdateTags } from '../../hooks/useUpdateTags';
import { useBindTagsToGroup } from '../../hooks/useBindTagsToGroup';
import { useUnbindTagsFromGroup } from '../../hooks/useUnbindTagsFromGroup';
import { useAppDispatch } from '../../../../../../shared/libs/redux';
import { useEditableTag } from '../../hooks/useEditableTag';
import { handleKeyDown } from '../../../../../../hooks/handleKeyDown';
import styles from './styles.module.scss';
import {
	templateTagGroupSelectors,
} from '../../../../../../entities/template/private-template-tag-group/model/slice/privateTemplateTagGroupSlice';

const animateLayoutChanges: AnimateLayoutChanges = (args) => defaultAnimateLayoutChanges({
	...args,
	wasDragging: true,
});

interface DroppableContainerProps extends ContainerProps {
	group: ITagGroup,
	disabled?: boolean;
	id: UniqueIdentifier;
	items: UniqueIdentifier[];
	style?: React.CSSProperties;
	getIndex(id: UniqueIdentifier): number,
	strategy?: SortingStrategy,
	wrapperStyle(args: {index: number}): React.CSSProperties;
	handle: boolean,
	getItemStyles?(args: {
		value: UniqueIdentifier;
		index: number;
		overIndex: number;
		isDragging: boolean;
		containerId: UniqueIdentifier;
		isSorting: boolean;
		isDragOverlay: boolean;
	}): React.CSSProperties;
	modalRef: React.RefObject<HTMLDivElement>;
}
const DroppableContainer: FC<DroppableContainerProps> = ({
	group,
	columns = 1,
	disabled,
	style,
	onClick,
	getIndex,
	strategy,
	wrapperStyle,
	getItemStyles,
	handle,
	...props
}) => {
	const {
		active,
		attributes,
		isDragging,
		listeners,
		over,
		setNodeRef,
		transition,
		transform,
	} = useSortable({
		id: group.id,
		data: {
			type: 'container',
			children: group.tags,
		},
		animateLayoutChanges,
	});
	const isOverContainer = over
		? (group.id === over.id && active?.data.current?.type !== 'container')
		|| group.tags?.some(tag => tag.id === over.id)
		: false;

	return (
		<Container
			ref={setNodeRef}
			style={{
				...style,
				transition,
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0.5 : undefined,
			}}
			hover={isOverContainer}
			handleProps={{
				...attributes,
				...listeners,
			}}
			getIndex={getIndex}
			group={group}
			wrapperStyle={wrapperStyle}
			handle={handle}
			getItemStyles={getItemStyles}
			onClick={onClick}
			{...props}
		/>
	);
};

export const TRASH_ID = 'void';

interface SortableItemProps {
	containerId: UniqueIdentifier;
	tag: ITag;
	index: number;
	handle: boolean;
	disabled?: boolean;
	style(args: any): React.CSSProperties;
	getIndex(id: UniqueIdentifier): number;
	wrapperStyle({ index }: {index: number}): React.CSSProperties;
	totalTags: number,
	className?: string,
}

export const SortableItem = ({
	disabled,
	tag,
	index,
	handle,
	style,
	containerId,
	getIndex,
	wrapperStyle,
	className,
	totalTags,
}: SortableItemProps) => {
	const {
		setNodeRef,
		setActivatorNodeRef,
		listeners,
		isDragging,
		isSorting,
		over,
		overIndex,
		transform,
		transition,
	} = useSortable({
		id: tag.id,
	});
	const mounted = useMountStatus();
	const mountedWhileDragging = isDragging && !mounted;

	const {
		handleTagNameChange,
		setEditableTag,
		setNewTagName,
		onDeletingTag,
		updateTagName,
		editableTag,
		newTagName,
	} = useEditableTag(tag.name, tag);

	const handleTagNameClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setEditableTag(tag.id);
		setNewTagName(tag.name);
	};

	const handleInputKeyDown = handleKeyDown({
		keyCode: 'Enter',
		isActive: newTagName.trim() !== '',
		action: updateTagName,
	});

	const handleNameKeyDown = handleKeyDown({
		keyCode: 'Enter',
		isActive: true,
		action: () => handleTagNameClick(new MouseEvent('click') as unknown as React.MouseEvent),
	});

	return (
		<Item
			ref={disabled ? undefined : setNodeRef}
			tag={tag}
			dragging={isDragging}
			sorting={isSorting}
			handle={handle}
			handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
			index={index}
			wrapperStyle={wrapperStyle({ index })}
			style={style({
				index,
				value: tag.name,
				isDragging,
				isSorting,
				overIndex: over ? getIndex(over.id) : overIndex,
				containerId,
			})}
			transition={transition}
			transform={transform}
			fadeIn={mountedWhileDragging}
			listeners={listeners}
			onRemove={onDeletingTag}
			key={tag.id}
			newTagName={newTagName}
			onDelete={onDeletingTag}
			className={index === totalTags - 1 ? `${className} lastTag` : className}
			updateTagName={updateTagName}
			handleTagNameChange={handleTagNameChange}
			handleInputKeyDown={handleInputKeyDown}
			handleNameKeyDown={handleNameKeyDown}
			handleNameClick={(e) => handleTagNameClick(e)}
			editableTag={editableTag}
			draggable
			paddingRight={0}
			groupId={String(containerId)}
		/>
	);
};

interface MultipleContainersProps {
	cancelDrop?: CancelDrop;
	columns?: number;
	containerStyle?: React.CSSProperties;
	coordinateGetter?: KeyboardCoordinateGetter;
	getItemStyles?(args: {
		value: UniqueIdentifier;
		index: number;
		overIndex: number;
		isDragging: boolean;
		containerId: UniqueIdentifier;
		isSorting: boolean;
		isDragOverlay: boolean;
	}): React.CSSProperties;
	items: ITagGroup[];
	handle?: boolean;
	strategy?: SortingStrategy;
	modifiers?: Modifiers;
	minimal?: boolean;
	scrollable?: boolean;
	vertical?: boolean;
	wrapperStyle?(args: {index: number}): React.CSSProperties;
}

export const MultipleContainers = ({
	cancelDrop,
	columns,
	handle = false,
	items: initialItems,
	containerStyle,
	coordinateGetter = multipleContainersCoordinateGetter,
	getItemStyles = () => ({}),
	wrapperStyle = () => ({}),
	minimal = false,
	modifiers,
	strategy = rectSwappingStrategy,
	vertical = false,
	scrollable,
}: MultipleContainersProps) => {
	const dispatch = useAppDispatch();
	const [items, setItems] = useState<ITagGroup[]>([]);
	const [openGroupId, setOpenGroupId] = useState<string>();
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
	const lastOverId = useRef<UniqueIdentifier | null>(null);
	const recentlyMovedToNewContainer = useRef(false);
	const activeContainerRef = useRef<ITagGroup | null>(null);
	const clonedItemsRef = useRef<ITagGroup[] | null>(null);
	const [sortableContextKey, setSortableContextKey] = useState(0);

	const openedIds = useSelector(templateTagGroupSelectors.getOpenedGroupsIds);

	// Чтобы убрать ResizeObserver уведомления
	useEffect(() => {
		const errorHandler = (e:any) => {
			if (
				e.message.includes(
					'ResizeObserver loop completed with undelivered notifications'
					|| 'ResizeObserver loop limit exceeded',
				)
			) {
				const resizeObserverErr = document.getElementById(
					'webpack-dev-server-client-overlay',
				);
				if (resizeObserverErr) {
					resizeObserverErr.style.display = 'none';
				}
			}
		};
		window.addEventListener('error', errorHandler);
		return () => {
			window.removeEventListener('error', errorHandler);
		};
	}, []);

	const onGroupClick = (id: string) => {
		setOpenGroupId(prevId => (prevId === id ? undefined : id));
	};
	const openCreateModal = () => {
		dispatch(templateTagGroupActions.setIsCreateGroupModalActive(true));
	};
	const { onUpdateTags } = useUpdateTags();
	const { unbindTagsFromGroup } = useUnbindTagsFromGroup();
	const { bindTagsToGroup } = useBindTagsToGroup();

	useEffect(() => {
		setItems(initialItems);
		setSortableContextKey(sortableContextKey + 1);
	}, [initialItems]);

	dispatch(templateTagGroupActions.setDeleteTagGroupFn(() => handleRemoveGroup));

	/**
	 * Пользовательская стратегия обнаружения столкновений оптимизирована для нескольких контейнеров
	 * - Во-первых, найдите любые падающие контейнеры, пересекающиеся с указателем.
	 * - Если нет, найдите пересекающиеся контейнеры с активным перетаскиванием.
	 * - Если нет пересекающихся контейнеров, верните последнее согласованное перекресток
	 */
	const collisionDetectionStrategy: CollisionDetection = useCallback(
		(args) => {
			if (activeId && items.find(item => item.id === activeId)) {
				return closestCenter({
					...args,
					droppableContainers: args.droppableContainers.filter(
						(container) => container.id in items,
					),
				});
			}

			// Start by finding any intersecting droppable
			const pointerIntersections = pointerWithin(args);
			const intersections = pointerIntersections.length > 0
				? pointerIntersections
				: rectIntersection(args);
			let overId = getFirstCollision(intersections, 'id');

			if (overId != null) {
				if (overId === TRASH_ID) {
					// If the intersecting droppable is the trash, return early
					// Remove this if you're not using trashable functionality in your app
					return intersections;
				}

				// Если overId принадлежит верхнему элементу (группе)
				if (items.some(item => item.id === overId)) {
					const containerItems = items.find(item => item.id === overId)?.tags;

					// Если контейнер сопоставлен и содержит элементы (columns 'A', 'B', 'C')
					if (containerItems && containerItems.length > 0) {
						// Return the closest droppable within that container
						overId = closestCenter({
							...args,
							droppableContainers: args.droppableContainers.filter(
								(container) => container.id !== overId
									&& containerItems.find(item => container.id === item.id),
							),
						})[0]?.id;
					}
				}

				lastOverId.current = overId;

				return [{ id: overId }];
			}

			// Когда перетаскиваемый элемент перемещается в новый контейнер, макет может сдвинуться,
			// а «Overid» может стать «null». Мы вручную устанавливаем кэшированную `loncoverid` на
			// идентификатор перетаскиваемого элемента, который был перенесен в новый контейнер,
			// в противном случае будет возвращен предыдущий` overid`, что может привести к
			// неправильному смену позиций.
			if (recentlyMovedToNewContainer.current) {
				lastOverId.current = activeId;
			}

			// If no droppable is matched, return the last match
			return lastOverId.current ? [{ id: lastOverId.current }] : [];
		},
		[activeId, items],
	);
	const sensors = useSensors(
		useSensor(MouseSensor),
		useSensor(TouchSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter,
		}),
	);

	const findContainer = (id: UniqueIdentifier) => {
		const container = items.find(group => {
			if (group.tags === null) return null;
			return group.tags && group.tags.some(tag => tag.id === id) || group.id === id;
		});
		return container ?? null;
	};

	const getIndex = (id: UniqueIdentifier) => {
		const container = findContainer(id);

		if (!container) {
			return -1;
		}

		const index = items.findIndex(group => group.id === container.id);

		return index;
	};

	const onDragCancel = () => {
		if (clonedItemsRef.current !== null) {
			setItems(clonedItemsRef.current);
		}

		setActiveId(null);
		activeContainerRef.current = null;

		clonedItemsRef.current = null;
	};

	useEffect(() => {
		requestAnimationFrame(() => {
			recentlyMovedToNewContainer.current = false;
		});
	}, [items]);

	const modalRef = useRef<HTMLDivElement>(null);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={collisionDetectionStrategy}
			measuring={measuring}
			onDragStart={({ active }) => {
				const activeContainer = findContainer(active.id);
				setActiveId(active.id);
				activeContainerRef.current = activeContainer;
				clonedItemsRef.current = items;
			}}
			onDragOver={({ active, over }) => {
				const overId = over?.id;
				if (overId == null || overId === TRASH_ID || items.some(item => item.id === active.id)) {
					return;
				}

				const overContainer = findContainer(overId);
				const activeContainer = findContainer(active.id);

				if (!overContainer || !activeContainer) {
					return;
				}

				// Записываем новый items
				if (activeContainer !== overContainer) {
					if (!openedIds.some(id => id === overContainer.id)) {
						dispatch(templateTagGroupActions.setOpenedGroupsIds(overContainer.id));
					}

					setItems((items) => {
						const activeItems = activeContainer.tags;
						const overItems = overContainer.tags;

						const overIndex = overItems.findIndex(tag => tag.id === overId);
						const activeIndex = activeItems.findIndex(tag => tag.id === active.id);

						let newIndex: number;

						if (items.some(item => item.id === overId)) {
							newIndex = overItems.length + 1;
						} else {
							const isBelowOverItem =	over
								&& active.rect.current.translated
								&& active.rect.current.translated.top
								> over.rect.top + over.rect.height;

							const modifier = isBelowOverItem ? 1 : 0;

							newIndex =	overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
						}

						recentlyMovedToNewContainer.current = true;

						return items.map(group => {
							if (group.id === activeContainer.id) {
								return ({
									...group,
									tags: activeContainer.tags.filter(tag => tag.id !== active.id),
								});
							}

							if (group.id === overContainer.id) {
								const newGroup = {
									...group,
									tags: [
										...group.tags.slice(0, newIndex),
										activeItems[activeIndex],
										...group.tags.slice(newIndex, group.tags.length),
									],
								};
								return ({
									...group,
									tags: [
										...group.tags.slice(0, newIndex),
										activeItems[activeIndex],
										...group.tags.slice(newIndex, group.tags.length),
									],
								});
							}

							return group;
						});
					});
				}
			}}
			onDragEnd={({ active, over }) => {
			/* 	была проблема при поиске активного (с которого перетаскиваем тег) контейнера, связанная с тем, что
				выше на onDragOver мы меняли items */
				const activeContainer = activeContainerRef.current;
				if	(!activeContainer) {
					setActiveId(null);
					console.log('не найден активный контейнер для перетаскивания');
					return;
				}

				const overId = over?.id;
				if (overId == null) {
					setActiveId(null);
					return;
				}

				const overContainer = findContainer(overId);
				if (!overContainer) {
					setActiveId(null);
					activeContainerRef.current = null;
					return;
				}

				if (clonedItemsRef.current) {
					setItems(clonedItemsRef.current);
				}

				const activeIndex = activeContainer.tags
					.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
					.findIndex(tag => tag.id === active.id);
				const overIndex = overContainer.tags
					.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
					.findIndex(tag => tag.id === over?.id);

				if (activeContainer.id === overContainer.id) {
					// здесь сортировка
					if (activeIndex !== overIndex) {
						const newItems = items.map(group => {
							if (group.id === overContainer.id) {
								return ({
									...group, tags: arrayMove(group.tags, activeIndex, overIndex),
								});
							}
							return group;
						});

						const updatedTags = arrayMove(activeContainer.tags, activeIndex, overIndex);
						onUpdateTags(updatedTags);
						setActiveId(null);
						setItems(newItems);
						return;
					}
				}

				// Если тег перемещается между группами
				if (activeContainer.id !== overContainer.id) {
					const movedTag = activeContainer.tags?.find(tag => tag.id === active.id);

					/* Чтобы привязать тег к группе, его надо сначала отвязать. Исключение составляют случаи с тегами
					* из группы отвязанных тегов - они уже отвязаны. Также при обновлении массива тегов мы разделяем
					* случаи, когда у overContainer (куда перетаскиваем) либо есть теги, либо массив тегов
					* пустой. */
					if (activeContainer.id !== TagGroupType.tagsWithoutGroup) {
						unbindTagsFromGroup(activeContainer.id, [Number(active.id)]);
					}

					if (overContainer.id !== TagGroupType.tagsWithoutGroup) {
						bindTagsToGroup(overContainer.id, [Number(active.id)]);
					}

					if (overContainer.tags && movedTag) {
						if (!clonedItemsRef.current) return;
						const prevTags = clonedItemsRef.current.find(
							group => group.id === overContainer.id,
						)?.tags;
						if (!prevTags) return;
						const sorted = prevTags.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
						const tags = [
							...sorted.slice(0, overIndex),
							movedTag,
							...sorted.slice(overIndex, sorted.length),
						];
						// const updatedTargetTags = [...overContainer.private-template-module-tag];
						//
						onUpdateTags(tags);
					} else if (movedTag && !overContainer.tags) {
						const newItems = items.map(group => {
							if (group.id === overContainer.id) {
								return ({
									...group,
									tags: [movedTag],
								});
							}
							return group;
						});
						setItems(newItems);
						onUpdateTags([movedTag]);
					}
				}
				setActiveId(null);
				activeContainerRef.current = null;
			}}
			cancelDrop={cancelDrop}
			onDragCancel={onDragCancel}
			modifiers={modifiers}
		>
			<div
				className={styles.wrapper}
				ref={modalRef}
			>
				{/* SortableContext используется для управления сортировкой элементов
				в списке при перетаскивании. Он нужен для того, чтобы предоставить необходимый контекст для
				компонента, который будет перетаскиваться и сортироваться. Это позволяет группе элементов
				понимать, как они должны вести себя при взаимодействии друг с другом. используется для оборачивания
				контейнера с элементами, которые должны быть сортируемыми. Внутри этого контейнера вы размещаете
				ваши сортируемые элементы, которые обычно реализованы с помощью компонента SortableItem.
				Вы передаете список items в SortableContext, чтобы он знал, какие элементы можно сортировать. */}
				<SortableContext
					items={items.map(group => group.id)}
					strategy={
						vertical
							? verticalListSortingStrategy
							: horizontalListSortingStrategy
					}
				>
					{items.map((group) => {
						const items = group.tags
							? group.tags.map(tag => tag.id)
							: [];

						return (
							<DroppableContainer
								key={group.id === TagGroupType.tagsWithoutGroup ? sortableContextKey : group.id}
								group={group}
								id={group.id}
								label={group.name}
								columns={columns}
								items={items}
								scrollable={scrollable}
								style={containerStyle}
								unstyled={minimal}
								onRemove={() => handleRemoveGroup(group.id)}
								onClick={() => onGroupClick(group.id)}
								getIndex={getIndex}
								strategy={strategy}
								wrapperStyle={wrapperStyle}
								handle={handle}
								getItemStyles={getItemStyles}
								modalRef={modalRef}
							/>
						);
					})}

					<button
						type="button"
						className={styles.createGroup}
						onClick={openCreateModal}
					>
						Создать группу
					</button>
				</SortableContext>
			</div>
		</DndContext>
	);

	function handleRemoveGroup(containerID: UniqueIdentifier) {
		dispatch(templateTagGroupActions.setDeletingGroupId(String(containerID)));
		dispatch(templateTagGroupActions.setIsDeleteGroupModalActive(true));
	}
};
